import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  PLATFORM_ID,
  SimpleChanges,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { IVideo } from '@api/models/video/video';
import { isPlatformBrowser } from '@angular/common';
import {
  IOverlayData,
  VideoPaywallOverlayComponent,
} from '@components/video-player/components/video-paywall-overlay/video-paywall-overlay.component';
import {environment} from '../../../../../../../../src/environments/environment';
import {LibVideoPlayerService} from '@components/video-player/services/lib-video-player.service';
import {BitmovinPlayerComponent} from '@components/video-player/bitmovin-player/bitmovin-player.component';
import {DownloadedVideoData} from 'bitmovin-player';
import {OverlayService} from '@lib-modules/overlays/overlay.service';
import {SetEmailDialogComponent} from '@lib-modules/dialogs/components/set-email-dialog/set-email-dialog.component';
import {PaywallType} from '@lib/models/paywall-type/paywall-type';
import {VideosHttpService} from '@api/http/videos/videos-http.service';
import {ApiErrors} from '@api/http/api-errors';
import {WebviewService} from '@utils/services/webview/webview.service';
import {AdService} from '@lib-modules/ads/services/ad.service';
import {EnvName} from '@utils/models/environment/env-name';
import {Router} from '@angular/router';
import {SubscriptionTrackingInfo} from '@lib/models/subscription/subscription-tracking-info';
import {SubscriptionTrackingService} from '@components/video-player/services/subscription-tracking.service';

interface IIntersectionEvent {
  target: Element;
  visible: boolean;
}

@Component({
  selector: 'lib-video-player',
  templateUrl: './video-player.component.html',
  styleUrls: ['./video-player.component.scss'],
})
export class VideoPlayerComponent implements OnInit, OnChanges {
  public static promoCode: string;
  @Input() public muted = true;
  @Input() public video: IVideo;
  @Input() public hlsUrl: string;
  @Input() public setupOnInit = false;
  @Input() public autostart = false;
  @Input() public showAd = true;
  @Input() public paywall = false;
  @Input() public user: any;
  @Input() public trackingInfo: SubscriptionTrackingInfo = null;
  @Output() public ready = new EventEmitter<DownloadedVideoData>();
  public viewingSessionBlocked = false;
  public previewClicked = false;
  public videoCanPlay = true;
  public player: BitmovinPlayerComponent;
  private canLoadPlayer = true;

  @ViewChild('bitmovinPlayer', { read: ViewContainerRef })
  set bmPlayerContainer(container: ViewContainerRef) {
    this.loadPlayer(container);
  }

  constructor(@Inject(PLATFORM_ID) protected platformId: any,
              private adService: AdService,
              private videoPlayerService: LibVideoPlayerService,
              private videoHttp: VideosHttpService,
              private overlayService: OverlayService,
              private router: Router,
              private subscriptionTrackingService: SubscriptionTrackingService
  ) { }

  public async ngOnInit(): Promise<void> {
    if (environment.name !== EnvName.Prod) {
      this.showAd = this.adService.displayRealAdsSetting && this.showAd;
    }
    await this.updateSubscriptionInfo();
  }

  public async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (changes.user && changes.user.previousValue !== this.user) {
      if (!isPlatformBrowser(this.platformId)) {
        return;
      }
      if (!changes.user?.firstChange) {
        this.viewingSessionBlocked = false;
        await this.updateSubscriptionInfo(true);
        if (!this.videoPlayerService.isSubscribed && this.paywall) {
          this.player?.pause();
        }
      } else {
        await this.updateSubscriptionInfo();
      }
    }
  }

  public async onCreateAbo(): Promise<void> {
    this.videoPlayerService.logout.emit();
    this.user = null;
    this.updateSubscriptionInfo();
    this.onPlayerClick();
  }

  public async onIntersect(event: IIntersectionEvent): Promise<void> {
    if (!this.player) {
      return;
    }
    if (event.visible) {
      if (!this.player.videoReady) {
        await this.player.setup();
        return;
      }
      if (this.autostart && this.videoCanPlay && !this.player.playedOnce) {
        await this.openViewingSession();
        this.player.play();
      }
    }
  }

  public onPreviewClick(): void {
    // Start video after player loaded
    this.autostart = true;
    this.previewClicked = true;
  }

  public async onPlayerClick(): Promise<void> {
    this.subscriptionTrackingService.setTrackingInfo(this.trackingInfo);
    await this.updateSubscriptionInfo();
    await this.openViewingSession();
    if (!this.paywall || !this.video.paywall) {
      return;
    }

    if (!this.user || this.user.deviceid) {
      this.navigateToPaywall();
      return;
    }
    if (!this.user.email || this.user.email.length === 0) {
      this.navigateToPaywall();
    }

    if (!this.videoPlayerService.isSubscribed) {
      await this.navigateToPaywall();
      return;
    }
  }

  public async onPaywallClick(event: MouseEvent): Promise<void> {
    event.stopPropagation();
    this.onPlayerClick();
  }

  public hasPreview(): boolean {
    return (
      this.video.preview?.resized_files?.length > 0 ||
      this.video.preview_url?.length > 0
    );
  }

  private async navigateToPaywall(): Promise<void> {
    // Handle natively in apps
    if (WebviewService.isWebview) {
      WebviewService.sendPaywallEvent(PaywallType.Video, VideoPlayerComponent.promoCode);
      return;
    }
    // Pass promo code if set
    let path = '/abo/subscribe';
    if (VideoPlayerComponent.promoCode) {
      path += '?promoCode=' + VideoPlayerComponent.promoCode;
      VideoPlayerComponent.promoCode = null;
    }
    await this.router.navigateByUrl(path);
    await this.updateSubscriptionInfo(true);
    if (this.user && this.videoPlayerService.isSubscribed) {
      this.autostart = true;
      await this.openViewingSession();
      await this.player?.play();
    }
  }

  private async updateSubscriptionInfo(fetch = false): Promise<void> {
    this.videoCanPlay = false;
    if (!this.paywall || !this.video.paywall) {
      this.videoCanPlay = true;
      this.player?.updateCanPlay(this.videoCanPlay);
      return;
    }
    if (!this.user) {
      return;
    }
    if (fetch || this.videoPlayerService.needsUpdate) {
      await this.videoPlayerService.updateSubscriptionInfo(this.user._id);
    }
    if (this.videoPlayerService.isSubscribed) {
      this.videoCanPlay = true;
    }
    this.player?.updateCanPlay(this.videoCanPlay);
  }

  private async loadPlayer(container: ViewContainerRef): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    if (container && this.canLoadPlayer) {
      this.player = await this.videoPlayerService.loadPlayer(container, {
        video: this.video,
        hlsUrl: this.hlsUrl,
        setupOnInit: this.setupOnInit,
        autostart: this.autostart,
        showAd: this.showAd,
        canPlay: this.videoCanPlay,
      });
      this.canLoadPlayer = false;
      this.player.playerClick.subscribe(() => this.onPlayerClick());
      this.player.ready.subscribe(async (data) => {
        this.ready.emit(data);
        await this.updateSubscriptionInfo();
        if (this.autostart && this.videoCanPlay) {
          await this.openViewingSession();
          this.player.play();
          console.log('muted', this.muted);
          if (this.muted) {
            this.player.mute();
          } else {
            this.player.unmute();
            const volumeString = localStorage.getItem('fanat-volume');
            const volume = volumeString ? Number(volumeString) : 50;
            this.player.setVolume(volume);
            console.log('volume', volume);
          }
        }
      });
    }
  }

  private async openViewingSession(): Promise<void> {
    // The video has no paywall or it was already played
    if (!this.video.paywall || this.player?.playedOnce || !this.user) {
      return;
    }
    // If paywall is active and user has no subscription the paywall overlay will be shown
    // Do not try to open a session in this case
    if (this.paywall && !this.videoPlayerService.isSubscribed) {
      return;
    }
    try {
      await this.videoHttp.postOpenViewingSession();
    } catch (e) {
      if (e.status === ApiErrors.AlreadyUsed.Status) {
        this.videoCanPlay = false;
        this.player.updateCanPlay(false);
        this.viewingSessionBlocked = true;
        throw e;
      }
      console.error(e);
    }
  }
}
